<template>
  <div>
    <header-component />
    <div class="px-4 py-4">

      <div class="flex justify-between items-center mb-5">
        <h2 class="text-lg font-bold text-gray-800">Branch Inventory</h2>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label class="block text-xs text-gray-500 mb-1">Branch</label>
          <select v-model="selectedBranchId" @change="fetchInventory"
            class="border border-gray-300 rounded px-3 py-2 text-sm">
            <option value="">Select branch...</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">Search Product</label>
          <input v-model="search" @input="onSearchInput" type="text" placeholder="Filter by name..."
            class="border border-gray-300 rounded px-3 py-2 text-sm" style="width:220px" />
        </div>
      </div>

      <div v-if="!selectedBranchId" class="text-center py-16 text-gray-400">
        Select a branch to view its inventory.
      </div>

      <div v-else-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-40">Stock Qty</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="inventory.length === 0">
              <td colspan="3" class="text-center py-12 text-gray-400">No products found.</td>
            </tr>
            <tr v-for="row in inventory" :key="row.product_id"
              class="border-b hover:bg-gray-50"
              :class="{ 'bg-red-50': row.quantity == 0 }">
              <td class="px-4 py-3 text-sm text-gray-800">{{ row.product_name }}</td>
              <td class="px-4 py-3 text-center text-sm"
                :class="row.quantity == 0 ? 'text-red-500 font-semibold' : 'text-gray-800'">
                {{ row.quantity }}
              </td>
              <td class="px-4 py-3 text-center">
                <button @click="openModal(row)"
                  class="text-blue-600 text-xs hover:underline font-medium">
                  Adjust Stock
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="text-xs text-gray-400 mt-2">
          Red rows have zero stock. Click Adjust Stock to record a change.
        </p>
      </div>

    </div>

    <!-- Adjustment Modal -->
    <div v-if="modal.open"
      style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:9999;"
      class="d-flex align-items-center justify-content-center">
      <div class="bg-white rounded shadow-lg p-4" style="width:420px;max-width:95vw;">
        <h5 class="mb-1 font-semibold text-gray-800">Adjust Stock</h5>
        <p class="text-sm text-gray-500 mb-4">{{ modal.productName }}</p>

        <div class="mb-3">
          <div class="flex justify-between text-xs text-gray-500 mb-3">
            <span>Current stock: <strong class="text-gray-800">{{ modal.currentQty }}</strong></span>
          </div>

          <label class="block text-xs text-gray-600 mb-1 font-medium">Reason</label>
          <select v-model="modal.adjustmentType" @change="onReasonChange"
            class="border border-gray-300 rounded px-3 py-2 text-sm w-full mb-3">
            <option value="">Select reason...</option>
            <option v-for="r in adjustmentReasons" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>

          <div v-if="modal.adjustmentType">
            <div class="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-xs text-blue-700 mb-3">
              {{ reasonHint }}
            </div>

            <label class="block text-xs text-gray-600 mb-1 font-medium">{{ quantityLabel }}</label>
            <input v-model.number="modal.quantity" type="number" min="0" step="0.001"
              :placeholder="quantityPlaceholder"
              class="border border-gray-300 rounded px-3 py-2 text-sm w-full mb-3" />

            <div v-if="modal.adjustmentType === 'adjustment' || modal.adjustmentType === 'opening_stock'"
              class="text-xs text-gray-500 mb-3">
              New stock will be set to <strong>{{ modal.quantity || 0 }}</strong>
              (change: <span :class="modal.quantity - modal.currentQty >= 0 ? 'text-green-600' : 'text-red-500'">
                {{ modal.quantity - modal.currentQty >= 0 ? '+' : '' }}{{ (modal.quantity - modal.currentQty).toFixed(3) }}
              </span>)
            </div>

            <label class="block text-xs text-gray-600 mb-1 font-medium">Notes <span class="font-normal text-gray-400">(optional)</span></label>
            <textarea v-model="modal.notes" rows="2" placeholder="Add a note..."
              class="border border-gray-300 rounded px-3 py-2 text-sm w-full resize-none"></textarea>
          </div>
        </div>

        <p v-if="modal.error" class="text-red-500 text-xs mb-2">{{ modal.error }}</p>

        <div class="flex gap-2 mt-1">
          <button @click="closeModal"
            class="border border-gray-300 rounded px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 flex-1">
            Cancel
          </button>
          <button @click="saveAdjustment" :disabled="modal.saving || !modal.adjustmentType"
            :class="confirmButtonClass"
            class="rounded px-4 py-2 text-sm text-white flex-1 disabled:opacity-50">
            {{ modal.saving ? 'Saving...' : confirmButtonLabel }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

var BASE = endpoints.LARAVEL_BASE_URL;
var searchTimer = null;

var REASONS = [
  { value: "opening_stock", label: "Opening Stock",      mode: "absolute", hint: "Set the initial stock count for this product at this branch.", button: "Set Opening Stock", buttonClass: "bg-blue-600 hover:bg-blue-700" },
  { value: "adjustment",    label: "Manual Correction",  mode: "absolute", hint: "Correct the stock count after a physical count or error.", button: "Save Correction", buttonClass: "bg-indigo-600 hover:bg-indigo-700" },
  { value: "surplus",       label: "Surplus Found",      mode: "add",      hint: "Extra units found during a stock count.", button: "Add Surplus", buttonClass: "bg-green-600 hover:bg-green-700" },
  { value: "damage",        label: "Damage / Spoilage",  mode: "deduct",   hint: "Remove units that are physically damaged or spoiled.", button: "Record Damage", buttonClass: "bg-orange-500 hover:bg-orange-600" },
  { value: "expired",       label: "Expired / Rusted",   mode: "deduct",   hint: "Remove units that have expired or degraded and are unsellable.", button: "Record Expiry", buttonClass: "bg-yellow-600 hover:bg-yellow-700" },
  { value: "loss",          label: "Loss / Theft",       mode: "deduct",   hint: "Remove units that are missing or stolen.", button: "Record Loss", buttonClass: "bg-red-600 hover:bg-red-700" },
];

export default {
  name: "BranchInventory",
  data() {
    return {
      branches: [],
      selectedBranchId: "",
      search: "",
      inventory: [],
      loading: false,
      adjustmentReasons: REASONS,
      modal: {
        open: false,
        productId: null,
        productName: "",
        currentQty: 0,
        adjustmentType: "",
        quantity: 0,
        notes: "",
        saving: false,
        error: "",
      },
    };
  },
  computed: {
    selectedReason() {
      var self = this;
      return REASONS.find(function(r) { return r.value === self.modal.adjustmentType; }) || null;
    },
    reasonHint() {
      return this.selectedReason ? this.selectedReason.hint : "";
    },
    quantityLabel() {
      if (!this.selectedReason) return "Quantity";
      if (this.selectedReason.mode === "absolute") return "New Stock Quantity";
      if (this.selectedReason.mode === "add")      return "Quantity to Add";
      return "Quantity to Deduct";
    },
    quantityPlaceholder() {
      if (!this.selectedReason) return "0";
      if (this.selectedReason.mode === "absolute") return "Enter new total quantity";
      if (this.selectedReason.mode === "add")      return "How many units were found?";
      return "How many units to remove?";
    },
    confirmButtonLabel() {
      return this.selectedReason ? this.selectedReason.button : "Save";
    },
    confirmButtonClass() {
      return this.selectedReason ? this.selectedReason.buttonClass : "bg-blue-600 hover:bg-blue-700";
    },
  },
  created() {
    this.fetchBranches();
  },
  methods: {
    async fetchBranches() {
      try {
        var res = await new Api().call("get", BASE + "/api/v1/branches");
        this.branches = res.data.data || res.data;
      } catch (e) {
        console.error(e);
      }
    },

    onSearchInput() {
      clearTimeout(searchTimer);
      var self = this;
      searchTimer = setTimeout(function() { self.fetchInventory(); }, 300);
    },

    async fetchInventory() {
      if (!this.selectedBranchId) return;
      this.loading = true;
      try {
        var params = new URLSearchParams({ branch_id: this.selectedBranchId });
        if (this.search) params.set("search", this.search);
        var res = await new Api().call("get", BASE + "/api/v1/branch-inventory?" + params.toString());
        this.inventory = res.data.inventory;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    openModal(row) {
      this.modal = {
        open: true,
        productId: row.product_id,
        productName: row.product_name,
        currentQty: row.quantity,
        adjustmentType: "",
        quantity: 0,
        notes: "",
        saving: false,
        error: "",
      };
    },

    onReasonChange() {
      this.modal.error = "";
      var reason = this.selectedReason;
      if (reason && reason.mode === "absolute") {
        this.modal.quantity = this.modal.currentQty;
      } else {
        this.modal.quantity = 0;
      }
    },

    closeModal() {
      this.modal.open = false;
    },

    async saveAdjustment() {
      this.modal.error = "";
      if (!this.modal.adjustmentType) {
        this.modal.error = "Please select a reason.";
        return;
      }
      if (this.modal.quantity < 0) {
        this.modal.error = "Quantity cannot be negative.";
        return;
      }
      if (this.modal.quantity === 0 && this.selectedReason && this.selectedReason.mode !== "absolute") {
        this.modal.error = "Please enter a quantity greater than 0.";
        return;
      }

      this.modal.saving = true;
      try {
        await new Api().call("post", BASE + "/api/v1/branch-inventory/adjust", {
          branch_id:       this.selectedBranchId,
          product_id:      this.modal.productId,
          quantity:        this.modal.quantity,
          adjustment_type: this.modal.adjustmentType,
          notes:           this.modal.notes || null,
        });

        window.notify({ message: "Stock adjusted successfully.", type: "success", title: "Done" });
        this.closeModal();
        this.fetchInventory();
      } catch (e) {
        var msg = (e && e.response && e.response.data && e.response.data.message) || "Failed to update stock.";
        this.modal.error = msg;
      } finally {
        this.modal.saving = false;
      }
    },
  },
};
</script>
